// services/adApi.js
// ---------------------------------------------------------------------------
// Fetches ad/campaign banners from the backend.
//
// Backend endpoint expected:
//   GET /api/ads?placement=<PLACEMENT>&tenantId=<tenantId>
//   (or via X-Tenant-ID header — both are sent for flexibility)
//
// Response shape (array):
//   [
//     {
//       id: "uuid",
//       title: "Summer Health Sale",
//       subtitle: "Up to 30% off on vitamins",   // optional
//       ctaLabel: "Shop Now",                    // optional
//       ctaUrl: "/pharmacy?category=vitamins",   // optional — relative or absolute
//       imageUrl: "https://...",                 // optional — banner image
//       backgroundColor: "#eef2ff",             // optional
//       accentColor: "#4f46e5",                 // optional
//       placement: "PRODUCT_PAGE",
//       active: true,
//     },
//     ...
//   ]
//
// Placements used by the frontend:
//   PRODUCT_PAGE       → shown on Product.js
//   CONSULTATION_PAGE  → shown on OnlineConsultation.js
// ---------------------------------------------------------------------------

import { API_BASE_URL as BASE_URL } from './apiBaseUrl';

// ---------------------------------------------------------------------------
// MOCK DATA — remove this block (and the fallback line below) once
// Rohith's /api/ads endpoint is live.
// ---------------------------------------------------------------------------
const MOCK_ADS = {
  PHARMACY_PAGE: [
    {
      id: 'mock-p1',
      tag: 'Limited Offer',
      title: 'Save 50–80% on\nBrand Medicines',
      subtitle: 'Generic alternatives available at a fraction of the cost',
      ctaLabel: 'Shop Now',
      ctaUrl: '/pharmacy',
      backgroundColor: 'linear-gradient(135deg, #1e1b4b 0%, #4f46e5 100%)',
      accentColor: '#fbbf24',
      textColor: '#ffffff',
      ctaBtnTextColor: '#1e1b4b',
      active: true,
    },
    {
      id: 'mock-p2',
      tag: 'Free Delivery',
      title: 'Zero Delivery Fee\non Orders Above ₹499',
      subtitle: 'Fast delivery to your doorstep · T&C apply',
      ctaLabel: 'Order Now',
      ctaUrl: '/pharmacy',
      backgroundColor: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
      accentColor: '#6ee7b7',
      textColor: '#ffffff',
      ctaBtnTextColor: '#064e3b',
      active: true,
    },
  ],
  PRODUCT_PAGE: [
    {
      id: 'mock-1',
      tag: 'Limited Offer',
      title: 'Save 50–80% on\nBrand Medicines',
      subtitle: 'Generic alternatives available at a fraction of the cost',
      ctaLabel: 'Shop Now',
      ctaUrl: '/pharmacy',
      backgroundColor: 'linear-gradient(135deg, #1e1b4b 0%, #4f46e5 100%)',
      accentColor: '#fbbf24',
      textColor: '#ffffff',
      ctaBtnTextColor: '#1e1b4b',
      active: true,
    },
    {
      id: 'mock-2',
      tag: 'Diabetes Care Month',
      title: 'Flat 20% Off on Every\nMedicine Order Above ₹1000',
      subtitle: 'For all registered ArogyaOne users · T&C apply',
      ctaLabel: 'View Offers',
      ctaUrl: '/pharmacy',
      backgroundColor: 'linear-gradient(135deg, #0c4a6e 0%, #0891b2 100%)',
      accentColor: '#ffffff',
      textColor: '#ffffff',
      ctaBtnTextColor: '#0c4a6e',
      active: true,
    },
    {
      id: 'mock-3',
      tag: 'Just Launched',
      title: 'Order Medicines on\nWhatsApp',
      subtitle: 'Send your prescription or "Hello" to get started instantly',
      ctaLabel: 'Try Now',
      ctaUrl: '/pharmacy',
      backgroundColor: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
      accentColor: '#6ee7b7',
      textColor: '#ffffff',
      ctaBtnTextColor: '#064e3b',
      active: true,
    },
  ],
  CONSULTATION_PAGE: [
    {
      id: 'mock-4',
      tag: 'New Users',
      title: 'First Consultation\nAbsolutely Free',
      subtitle: 'Connect with certified doctors via video or chat · No cost',
      ctaLabel: 'Book Now',
      ctaUrl: '/services/book-appointment?mode=online&secondOpinion=false',
      backgroundColor: 'linear-gradient(135deg, #0c4a6e 0%, #7c3aed 100%)',
      accentColor: '#fbbf24',
      textColor: '#ffffff',
      ctaBtnTextColor: '#1e1b4b',
      active: true,
    },
    {
      id: 'mock-5',
      tag: 'Health Packages',
      title: 'Full Body Checkup\nStarting at ₹999',
      subtitle: 'Book at nearby hospitals & clinics · Results in 24 hours',
      ctaLabel: 'Explore Packages',
      ctaUrl: '/services/hospitals',
      backgroundColor: 'linear-gradient(135deg, #4c1d95 0%, #be185d 100%)',
      accentColor: '#f9a8d4',
      textColor: '#ffffff',
      ctaBtnTextColor: '#4c1d95',
      active: true,
    },
  ],
};
// ---------------------------------------------------------------------------

/**
 * Fetch active ads for a given placement.
 * Falls back to mock data if the backend returns a non-OK response or errors.
 * Once /api/ads is live, remove MOCK_ADS above and change the fallback
 * line inside the catch/non-ok blocks from `MOCK_ADS[placement] ?? []` to `[]`.
 *
 * @param {string} tenantId
 * @param {'PRODUCT_PAGE'|'CONSULTATION_PAGE'} placement
 * @returns {Promise<Array>}
 */
export const getAds = async (tenantId, placement) => {
  if (!tenantId || !placement) return [];
  try {
    const params = new URLSearchParams({ placement });
    const res = await fetch(`${BASE_URL}/ads?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
      },
    });
    if (!res.ok) return MOCK_ADS[placement] ?? [];            // ← remove mock fallback when live
    const data = await res.json();
    return Array.isArray(data) ? data.filter((ad) => ad.active !== false) : [];
  } catch {
    return MOCK_ADS[placement] ?? [];                         // ← remove mock fallback when live
  }
};